import { Box, Button, InputAdornment, Link, MenuItem, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import DataTable from '../components/DataTable';
import PageHeader from '../components/PageHeader';
import { api } from '../shared/service/dataprovider';
import tokens from '../shared/theme/tokens';

const money = value =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '\u2014'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));

const RESIDENCY_OPTIONS = ['Approved', 'Current', 'Month to Month'];

const depositLabel = row => {
  if (row.calculatedAmount === null || row.calculatedAmount === undefined) {
    return row.waiverAvailable ? 'Waiver' : '\u2014';
  }
  const amount = money(row.calculatedAmount);
  return row.waiverAvailable ? `${amount} or waiver` : amount;
};

export default function DepositAssessment() {
  const [propertyId, setPropertyId] = useState('');
  const [unit, setUnit] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [residencyStatuses, setResidencyStatuses] = useState(['Approved']);
  const [tab, setTab] = useState('missing');
  const [confirmRow, setConfirmRow] = useState(null);
  const [chargeError, setChargeError] = useState('');
  const [removeRow, setRemoveRow] = useState(null);
  const [removeError, setRemoveError] = useState('');
  const [reapplyRow, setReapplyRow] = useState(null);
  const [reapplyError, setReapplyError] = useState('');
  const queryClient = useQueryClient();
  const charge = useMutation({
    mutationFn: leaseId => api.post('/v1/admin/reports/deposit-assessment/charge', { leaseId }),
    onSuccess: () => {
      setConfirmRow(null);
      setChargeError('');
      queryClient.invalidateQueries({ queryKey: ['depositAssessment'] });
      queryClient.invalidateQueries({ queryKey: ['depositAssessmentApplied'] });
      queryClient.invalidateQueries({ queryKey: ['depositRequirementsPosted'] });
    },
    onError: error => {
      setChargeError(error?.response?.data?.message || 'The deposit was not created.');
    },
  });
  const removeCharge = useMutation({
    mutationFn: leaseId => api.post('/v1/admin/reports/deposit-assessment/remove', { leaseId }),
    onSuccess: () => {
      setRemoveRow(null);
      setRemoveError('');
      queryClient.invalidateQueries({ queryKey: ['depositAssessment'] });
      queryClient.invalidateQueries({ queryKey: ['depositAssessmentApplied'] });
      queryClient.invalidateQueries({ queryKey: ['depositRequirementsPosted'] });
    },
    onError: error => {
      setRemoveError(error?.response?.data?.message || 'The deposit was not removed.');
    },
  });
  const reapplyCharge = useMutation({
    mutationFn: leaseId => api.post('/v1/admin/reports/deposit-assessment/reapply', { leaseId }),
    onSuccess: () => {
      setReapplyRow(null);
      setReapplyError('');
      queryClient.invalidateQueries({ queryKey: ['depositAssessment'] });
      queryClient.invalidateQueries({ queryKey: ['depositAssessmentApplied'] });
      queryClient.invalidateQueries({ queryKey: ['depositRequirementsPosted'] });
    },
    onError: error => {
      setReapplyError(error?.response?.data?.message || 'The deposit was not reapplied.');
    },
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user'],
    queryFn: () => api.get('/v1/admin/profile'),
  });
  const isSuperAdmin = Boolean(profile?.data?.isSuperAdmin);

  const { data: properties } = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get('/v1/admin/properties'),
    enabled: isSuperAdmin,
  });

  const {
    data: posted,
    isLoading: postedLoading,
    isFetching: postedFetching,
  } = useQuery({
    queryKey: ['depositRequirementsPosted', propertyId],
    queryFn: () =>
      api.get('/v1/admin/reports/deposit-assessment/posted', {
        params: { propertyId: propertyId || undefined },
      }),
    enabled: isSuperAdmin,
  });

  const { data: appliedData, isLoading: appliedLoading, isFetching: appliedFetching } = useQuery({
    queryKey: ['depositAssessmentApplied', propertyId, from, residencyStatuses],
    queryFn: () =>
      api.get('/v1/admin/reports/deposit-assessment', {
        params: {
          propertyId: propertyId || undefined,
          from: from || undefined,
          residencyStatus: residencyStatuses.join(','),
          applied: '1',
        },
      }),
    enabled: isSuperAdmin && tab === 'applied',
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['depositAssessment', propertyId, from, residencyStatuses],
    queryFn: () =>
      api.get('/v1/admin/reports/deposit-assessment', {
        params: {
          propertyId: propertyId || undefined,
          from: from || undefined,
          residencyStatus: residencyStatuses.join(','),
        },
      }),
    enabled: isSuperAdmin,
  });

  useEffect(() => {
    if (!from && data?.data?.from) setFrom(data.data.from);
  }, [data, from]);

  const propertyNameById = useMemo(() => {
    const names = new Map();
    for (const property of properties?.data ?? []) names.set(property.id, property.name);
    return names;
  }, [properties]);

  const rows = useMemo(() => {
    const unitQuery = unit.trim().toLowerCase();
    const source = data?.data?.rows ?? [];
    const byHousehold = new Map();
    for (const row of source) {
      if (to && row.applicationDate && row.applicationDate > to) continue;
      if (
        unitQuery &&
        !String(row.unitNumber || '')
          .toLowerCase()
          .includes(unitQuery)
      )
        continue;
      const key = [row.leaseId, row.propertyId, row.unitNumber].join('|');
      const existing = byHousehold.get(key);
      if (existing) {
        if (row.applicantName && !existing.names.includes(row.applicantName)) existing.names.push(row.applicantName);
        if (!existing.leaseId && row.leaseId) existing.leaseId = row.leaseId;
        if (row.residencyStatus && !existing.residencyStatuses.includes(row.residencyStatus)) {
          existing.residencyStatuses.push(row.residencyStatus);
        }
        if (existing.globalDeposit == null && row.globalDeposit != null) existing.globalDeposit = row.globalDeposit;
        if (row.applicationDate && (!existing.applicationDate || row.applicationDate < existing.applicationDate)) {
          existing.applicationDate = row.applicationDate;
        }
      } else {
        byHousehold.set(key, {
          key,
          names: row.applicantName ? [row.applicantName] : [],
          leaseId: row.leaseId,
          propertyName: row.propertyName,
          unitNumber: row.unitNumber,
          residencyStatuses: row.residencyStatus ? [row.residencyStatus] : [],
          applicationDate: row.applicationDate,
          decision: row.decision,
          recommendation: row.recommendation,
          monthlyRent: row.monthlyRent,
          globalDeposit: row.globalDeposit,
          calculatedAmount: row.calculatedAmount,
          waiverAvailable: row.waiverAvailable,
        });
      }
    }
    return Array.from(byHousehold.values());
  }, [data, unit, to]);

  const appliedRows = useMemo(() => {
    const unitQuery = unit.trim().toLowerCase();
    const source = appliedData?.data?.rows ?? [];
    const byHousehold = new Map();
    for (const row of source) {
      if (to && row.applicationDate && row.applicationDate > to) continue;
      if (unitQuery && !String(row.unitNumber || '').toLowerCase().includes(unitQuery)) continue;
      const key = [row.leaseId, row.propertyId, row.unitNumber].join('|');
      const existing = byHousehold.get(key);
      if (existing) {
        if (row.applicantName && !existing.names.includes(row.applicantName)) existing.names.push(row.applicantName);
        if (row.depositAmount != null) existing.depositAmount = row.depositAmount;
        if (existing.monthlyRent == null && row.monthlyRent != null) existing.monthlyRent = row.monthlyRent;
      } else {
        byHousehold.set(key, {
          key,
          names: row.applicantName ? [row.applicantName] : [],
          leaseId: row.leaseId,
          propertyName: row.propertyName,
          unitNumber: row.unitNumber,
          monthlyRent: row.monthlyRent,
          decision: row.decision,
          recommendation: row.recommendation,
          depositAmount: row.depositAmount,
        });
      }
    }
    return Array.from(byHousehold.values());
  }, [appliedData, unit, to]);


  const postedRows = useMemo(() => {
    const unitQuery = unit.trim().toLowerCase();
    return (posted?.data?.rows ?? []).filter(row => {
      if (!row.removedAt) return false;
      if (!unitQuery) return true;
      return String(row.unitNumber || '')
        .toLowerCase()
        .includes(unitQuery);
    });
  }, [posted, unit]);

  const postedColumns = [
    {
      key: 'unitNumber',
      header: 'Unit',
      render: row =>
        row.leaseId ? (
          <Link href={`https://resprop.myresman.com/#/Residents/Detail/${row.leaseId}`} target="_blank" rel="noopener" underline="hover">
            {row.unitNumber || '\u2014'}
          </Link>
        ) : (
          row.unitNumber || '\u2014'
        ),
      sx: { minWidth: 90 },
    },
    { key: 'propertyName', header: 'Property', render: row => row.propertyName || '\u2014', sx: { minWidth: 180 } },
    { key: 'monthlyRent', header: 'Unit rent', render: row => money(row.monthlyRent), sx: { minWidth: 120 } },
    { key: 'amount', header: 'Amount', render: row => money(row.amount), sx: { minWidth: 120 } },
    {
      key: 'postedAt',
      header: 'Posted',
      render: row => (row.postedAt ? moment(row.postedAt).format('MM/DD/YYYY h:mm A') : '\u2014'),
      sx: { minWidth: 180 },
    },
    { key: 'postedBy', header: 'Posted by', render: row => row.postedBy || '\u2014', sx: { minWidth: 200 } },
    {
      key: 'removedAt',
      header: 'Removed',
      render: row => (row.removedAt ? moment(row.removedAt).format('MM/DD/YYYY h:mm A') : '\u2014'),
      sx: { minWidth: 180 },
    },
    { key: 'removedBy', header: 'Removed by', render: row => row.removedBy || '\u2014', sx: { minWidth: 200 } },
    {
      key: 'recommendation',
      header: 'SafeRent recommendation',
      render: row => (
        <Typography variant="body2" sx={{ maxWidth: 420, whiteSpace: 'normal' }}>
          {row.recommendation || '\u2014'}
        </Typography>
      ),
      sx: { minWidth: 320 },
    },
    {
      key: 'reapplyDeposit',
      header: '',
      render: row =>
        row.leaseId && Number(row.amount) > 0.01 ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setReapplyError('');
              setReapplyRow(row);
            }}
          >
            Reapply deposit
          </Button>
        ) : null,
      sx: { minWidth: 170 },
    },
  ];

  const appliedColumns = [
    {
      key: 'names',
      header: 'Name',
      render: row => {
        const label = row.names.join(', ') || '\u2014';
        return row.leaseId ? (
          <Link href={`https://resprop.myresman.com/#/Residents/Detail/${row.leaseId}`} target="_blank" rel="noopener" underline="hover">
            {label}
          </Link>
        ) : (
          label
        );
      },
      sx: { minWidth: 220, whiteSpace: 'normal' },
    },
    { key: 'propertyName', header: 'Property', render: row => row.propertyName || '\u2014', sx: { minWidth: 180 } },
    { key: 'unitNumber', header: 'Unit', render: row => row.unitNumber || '\u2014', sx: { minWidth: 90 } },
    { key: 'monthlyRent', header: 'Unit rent', render: row => money(row.monthlyRent), sx: { minWidth: 120 } },
    { key: 'depositAmount', header: 'Deposit on ledger', render: row => money(row.depositAmount), sx: { minWidth: 150 } },
    { key: 'decision', header: 'SafeRent', render: row => row.decision || '\u2014', sx: { minWidth: 120 } },
    {
      key: 'recommendation',
      header: 'SafeRent recommendation',
      render: row => (
        <Typography variant="body2" sx={{ maxWidth: 420, whiteSpace: 'normal' }}>
          {row.recommendation || '\u2014'}
        </Typography>
      ),
      sx: { minWidth: 320 },
    },
    {
      key: 'removeDeposit',
      header: '',
      render: row =>
        Number(row.depositAmount) > 0.01 ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setRemoveError('');
              setRemoveRow(row);
            }}
          >
            Remove deposit
          </Button>
        ) : null,
      sx: { minWidth: 160 },
    },
  ];

  const columns = [
    {
      key: 'names',
      header: 'Name',
      render: row => {
        const label = row.names.join(', ') || '\u2014';
        return row.leaseId ? (
          <Link href={`https://resprop.myresman.com/#/Residents/Detail/${row.leaseId}`} target="_blank" rel="noopener" underline="hover">
            {label}
          </Link>
        ) : (
          label
        );
      },
      sx: { minWidth: 220, whiteSpace: 'normal' },
    },
    { key: 'propertyName', header: 'Property', render: row => row.propertyName || '\u2014', sx: { minWidth: 180 } },
    { key: 'unitNumber', header: 'Unit', render: row => row.unitNumber || '\u2014', sx: { minWidth: 90 } },
    {
      key: 'residencyStatuses',
      header: 'Residency',
      render: row => row.residencyStatuses.join(', ') || '\u2014',
      sx: { minWidth: 140 },
    },
    { key: 'monthlyRent', header: 'Unit rent', render: row => money(row.monthlyRent), sx: { minWidth: 120 } },
    { key: 'globalDeposit', header: 'Global deposit', render: row => money(row.globalDeposit), sx: { minWidth: 140 } },
    {
      key: 'applicationDate',
      header: 'Application date',
      render: row => (row.applicationDate ? moment(row.applicationDate).format('MM/DD/YYYY') : '\u2014'),
      sx: { minWidth: 140 },
    },
    { key: 'decision', header: 'SafeRent', render: row => row.decision || '\u2014', sx: { minWidth: 120 } },
    {
      key: 'recommendation',
      header: 'SafeRent recommendation',
      render: row => (
        <Typography variant="body2" sx={{ maxWidth: 420, whiteSpace: 'normal' }}>
          {row.recommendation || '\u2014'}
        </Typography>
      ),
      sx: { minWidth: 320 },
    },
    { key: 'calculatedAmount', header: 'Calculated deposit', render: depositLabel, sx: { minWidth: 170 } },
    {
      key: 'createDeposit',
      header: '',
      render: row =>
        row.leaseId && Number(row.calculatedAmount) > 0.01 ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setChargeError('');
              setConfirmRow(row);
            }}
          >
            Create deposit
          </Button>
        ) : null,
      sx: { minWidth: 160 },
    },
  ];

  if (profileLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <Box>
      <PageHeader
        title="Deposit assessment"
        action={
          <Select
            size="small"
            displayEmpty
            value={propertyId}
            onChange={e => setPropertyId(e.target.value)}
            renderValue={value => propertyNameById.get(value) || 'All Properties'}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            sx={{ minWidth: 260, backgroundColor: 'background.paper' }}
          >
            <MenuItem value="">All Properties</MenuItem>
            {(properties?.data ?? []).map(property => (
              <MenuItem key={property.id} value={property.id}>
                {property.name}
              </MenuItem>
            ))}
          </Select>
        }
      />
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          value={unit}
          onChange={e => setUnit(e.target.value)}
          placeholder="Unit"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 180, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
        <Select
          multiple
          size="small"
          value={residencyStatuses}
          onChange={e => {
            const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
            if (next.length) setResidencyStatuses(next);
          }}
          renderValue={selected => selected.join(', ')}
          sx={{ minWidth: 220, backgroundColor: 'background.paper' }}
        >
          {RESIDENCY_OPTIONS.map(status => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
      </Box>
      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="missing" label="No deposit on the ledger" />
        <Tab value="applied" label="Deposit Applied by TenantPort" />
      </Tabs>
      {tab === 'missing' ? (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={row => row.key}
          loading={isLoading || isFetching}
          emptyContent="No approved applicants without a deposit in this range."
          minWidth={1440}
        />
      ) : (
        <Box>
          <DataTable
            columns={appliedColumns}
            rows={appliedRows}
            rowKey={row => row.key}
            loading={appliedLoading || appliedFetching}
            emptyContent="No approved applicants with a deposit in this range."
            minWidth={1200}
          />
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Removed
          </Typography>
          <DataTable
            columns={postedColumns}
            rows={postedRows}
            rowKey={row => row.leaseId}
            loading={postedLoading || postedFetching}
            emptyContent="No deposit requirements removed from TenantPort yet."
            minWidth={1100}
          />
        </Box>
      )}
      <ConfirmDialog
        open={Boolean(confirmRow)}
        onClose={() => {
          if (charge.isPending) return;
          setConfirmRow(null);
          setChargeError('');
        }}
        title="Create deposit"
        body={
          confirmRow
            ? `Create a ${money(confirmRow.calculatedAmount)} security deposit on ${confirmRow.propertyName || 'this property'}, unit ${confirmRow.unitNumber || '\u2014'}.${chargeError ? ` ${chargeError}` : ''}`
            : ''
        }
        confirmLabel="Create deposit"
        cancelLabel="Cancel"
        loading={charge.isPending}
        onConfirm={() => confirmRow?.leaseId && charge.mutate(confirmRow.leaseId)}
      />
      <ConfirmDialog
        open={Boolean(removeRow)}
        onClose={() => {
          if (removeCharge.isPending) return;
          setRemoveRow(null);
          setRemoveError('');
        }}
        title="Remove deposit"
        body={
          removeRow
            ? `Set the ${money(removeRow.depositAmount)} security deposit to $0 on ${removeRow.propertyName || 'this property'}, unit ${removeRow.unitNumber || '\u2014'}.${removeError ? ` ${removeError}` : ''}`
            : ''
        }
        confirmLabel="Remove deposit"
        cancelLabel="Cancel"
        loading={removeCharge.isPending}
        onConfirm={() => removeRow?.leaseId && removeCharge.mutate(removeRow.leaseId)}
      />
      <ConfirmDialog
        open={Boolean(reapplyRow)}
        onClose={() => {
          if (reapplyCharge.isPending) return;
          setReapplyRow(null);
          setReapplyError('');
        }}
        title="Reapply deposit"
        body={
          reapplyRow
            ? `Put the ${money(reapplyRow.amount)} security deposit back on ${reapplyRow.propertyName || 'this property'}, unit ${reapplyRow.unitNumber || '\u2014'}.${reapplyError ? ` ${reapplyError}` : ''}`
            : ''
        }
        confirmLabel="Reapply deposit"
        cancelLabel="Cancel"
        loading={reapplyCharge.isPending}
        onConfirm={() => reapplyRow?.leaseId && reapplyCharge.mutate(reapplyRow.leaseId)}
      />
    </Box>
  );
}
